import { useForm, router } from "@inertiajs/react"
import type { ReactNode } from "react"

import { AdminLayout } from "@/layouts/AdminLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import type { GuestProfileDetail, PropertySummary } from "@/lib/types"

interface Props {
  property: PropertySummary
  profile: GuestProfileDetail | null
  urls: {
    submit: string
    cancel: string
  }
}

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "pt", label: "Portuguese" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "it", label: "Italian" },
  { value: "nl", label: "Dutch" },
  { value: "ru", label: "Russian" },
  { value: "zh", label: "Chinese" },
  { value: "ja", label: "Japanese" },
  { value: "ar", label: "Arabic" },
]

export default function GuestProfileForm({ property, profile, urls }: Props) {
  const isEdit = profile !== null
  const { data, setData, post, patch, processing, errors } = useForm({
    name:        profile?.name ?? "",
    email:       profile?.email ?? "",
    phone:       profile?.phone ?? "",
    language:    profile?.language ?? "",
    notes:       profile?.notes ?? "",
    preferences: {
      dietary:   (profile?.preferences?.dietary ?? "") as string,
      interests: (profile?.preferences?.interests ?? "") as string,
      notes:     (profile?.preferences?.notes ?? "") as string,
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isEdit) {
      patch(urls.submit, { preserveScroll: true })
    } else {
      post(urls.submit)
    }
  }

  return (
    <div className="mx-auto max-w-xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? "Edit Guest Profile" : "New Guest Profile"}
        </h1>
        <p className="mt-0.5 text-sm text-muted-foreground">{property.name}</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="grid gap-1.5">
          <Label htmlFor="name">Name <span className="text-destructive">*</span></Label>
          <Input
            id="name"
            value={data.name}
            onChange={(e) => setData("name", e.target.value)}
            placeholder="Guest full name"
            required
          />
          {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={data.email}
            onChange={(e) => setData("email", e.target.value)}
            placeholder="guest@example.com"
          />
          {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            type="tel"
            value={data.phone}
            onChange={(e) => setData("phone", e.target.value)}
            placeholder="+351 9XX XXX XXX"
          />
          {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="language">Language</Label>
          <Select
            value={data.language}
            onValueChange={(v) => setData("language", v)}
          >
            <SelectTrigger id="language">
              <SelectValue placeholder="Select language…" />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.language && <p className="text-xs text-destructive">{errors.language}</p>}
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            rows={4}
            value={data.notes}
            onChange={(e) => setData("notes", e.target.value)}
            placeholder="Internal notes about this guest…"
          />
          {errors.notes && <p className="text-xs text-destructive">{errors.notes}</p>}
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="preferences_notes">Preferences & Notes</Label>
          <Textarea
            id="preferences_notes"
            rows={3}
            placeholder="Dietary needs, interests, special requests..."
            value={data.preferences?.notes ?? ""}
            onChange={e => setData("preferences", { ...data.preferences, notes: e.target.value })}
            aria-label="Preferences and notes"
          />
          {errors.preferences && <p className="text-xs text-destructive">{errors.preferences}</p>}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => router.visit(urls.cancel)}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? "Saving…" : isEdit ? "Save changes" : "Create guest profile"}
          </Button>
        </div>
      </form>
    </div>
  )
}

GuestProfileForm.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
